/**
 * CareTeamMemberPage
 * Individual care team member details
 * BP-06-03: Care Team & Goals Frontend
 */

import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  Trash2,
  Phone,
  Mail,
  MapPin,
  Building,
  Star,
  Calendar,
  ExternalLink,
  Loader2,
  AlertTriangle,
} from 'lucide-react';
import { useCareTeamMember, useDeleteCareTeamMember, useUpdateCareTeamMember, useCareTeamTypes } from '@/features/care-team/hooks/useCareTeam';
import { CareTeamMemberForm } from '../components';
import { CARE_TEAM_CATEGORY_LABELS } from '@/features/care-team/types/careTeamTypes';
import type { CareTeamMemberFormData } from '@/features/care-team/types/careTeamTypes';

export function CareTeamMemberPage() {
  const { memberId } = useParams<{ memberId: string }>();
  const navigate = useNavigate();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const { data: member, isLoading, error } = useCareTeamMember(memberId!);
  const { data: types } = useCareTeamTypes();
  const deleteMutation = useDeleteCareTeamMember();
  const updateMutation = useUpdateCareTeamMember();

  const handleDelete = async () => {
    if (!memberId) return;
    await deleteMutation.mutateAsync(memberId);
    navigate('/family/care-team');
  };

  const handleUpdate = async (data: CareTeamMemberFormData) => {
    if (!memberId) return;
    await updateMutation.mutateAsync({ memberId, data });
    setShowEditModal(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-sage" />
      </div>
    );
  }

  if (error || !member) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8 text-center">
        <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-terracotta" />
        <h2 className="text-xl font-semibold text-navy mb-2">Member Not Found</h2>
        <p className="text-warm-gray mb-4">Unable to load care team member details.</p>
        <button
          onClick={() => navigate('/family/care-team')}
          className="text-sage hover:text-sage-dark"
        >
          ← Back to Care Team
        </button>
      </div>
    );
  }

  const memberType = types?.find((t) => t.id === member.memberTypeId);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Back Button */}
      <button
        onClick={() => navigate('/family/care-team')}
        className="inline-flex items-center gap-2 text-warm-gray hover:text-navy mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Care Team
      </button>

      {/* Header Card */}
      <div className="bg-white rounded-2xl border border-warm-gray/10 overflow-hidden mb-6">
        <div className="p-6 sm:p-8">
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              {/* Avatar */}
              <div className="w-16 h-16 rounded-xl bg-sage/20 flex items-center justify-center flex-shrink-0">
                <span className="text-xl font-semibold text-sage-dark">
                  {member.name.split(' ').map((n) => n[0]).join('').slice(0, 2)}
                </span>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h1 className="text-2xl font-display font-semibold text-navy">{member.name}</h1>
                  {member.isPrimary && (
                    <span className="px-2 py-0.5 bg-sage/10 text-sage-dark text-xs font-medium rounded-full">
                      Primary
                    </span>
                  )}
                </div>
                {member.title && <p className="text-navy-light font-medium">{member.title}</p>}
                {memberType && (
                  <p className="text-warm-gray text-sm">
                    {memberType.name} • {CARE_TEAM_CATEGORY_LABELS[memberType.category]}
                  </p>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowEditModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2 border-2 border-sage text-sage hover:bg-sage/10 rounded-lg transition-colors"
              >
                <Edit className="w-4 h-4" />
                Edit
              </button>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="p-2 text-warm-gray hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="border-t border-warm-gray/10 px-6 sm:px-8 py-4 bg-cream/50">
          <div className="flex flex-wrap gap-6">
            {member.phone && (
              <a
                href={`tel:${member.phone}`}
                className="flex items-center gap-2 text-navy hover:text-sage transition-colors"
              >
                <Phone className="w-4 h-4" />
                {member.phone}
              </a>
            )}
            {member.email && (
              <a
                href={`mailto:${member.email}`}
                className="flex items-center gap-2 text-navy hover:text-sage transition-colors"
              >
                <Mail className="w-4 h-4" />
                {member.email}
              </a>
            )}
            {member.organization && (
              <span className="flex items-center gap-2 text-navy">
                <Building className="w-4 h-4" />
                {member.organization}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Details Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Professional Info */}
        <div className="bg-white rounded-xl border border-warm-gray/10 p-6">
          <h2 className="text-lg font-display font-semibold text-navy mb-4">Professional Information</h2>
          <dl className="space-y-4">
            {member.specialty && (
              <div>
                <dt className="text-sm text-warm-gray">Specialty</dt>
                <dd className="text-navy font-medium">{member.specialty}</dd>
              </div>
            )}
            {member.npiNumber && (
              <div>
                <dt className="text-sm text-warm-gray">NPI Number</dt>
                <dd className="text-navy font-medium">{member.npiNumber}</dd>
              </div>
            )}
            {member.licenseNumber && (
              <div>
                <dt className="text-sm text-warm-gray">License Number</dt>
                <dd className="text-navy font-medium">{member.licenseNumber}</dd>
              </div>
            )}
            {member.startDate && (
              <div>
                <dt className="text-sm text-warm-gray">Started Care</dt>
                <dd className="text-navy font-medium flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-warm-gray" />
                  {new Date(member.startDate).toLocaleDateString()}
                </dd>
              </div>
            )}
            {member.rating && (
              <div>
                <dt className="text-sm text-warm-gray">Your Rating</dt>
                <dd className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-4 h-4 ${
                        i < member.rating! ? 'fill-yellow-400 text-yellow-400' : 'text-gray-200'
                      }`}
                    />
                  ))}
                </dd>
              </div>
            )}
          </dl>
        </div>

        {/* Address & Notes */}
        <div className="space-y-6">
          {member.address && (
            <div className="bg-white rounded-xl border border-warm-gray/10 p-6">
              <h2 className="text-lg font-display font-semibold text-navy mb-4">Address</h2>
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-warm-gray flex-shrink-0 mt-0.5" />
                <p className="text-navy whitespace-pre-line">{member.address}</p>
              </div>
            </div>
          )}

          {member.notes && (
            <div className="bg-white rounded-xl border border-warm-gray/10 p-6">
              <h2 className="text-lg font-display font-semibold text-navy mb-4">Notes</h2>
              <p className="text-navy-light">{member.notes}</p>
            </div>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-navy/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-lifted w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <CareTeamMemberForm
                familyId={member.familyId}
                memberId={member.id}
                memberTypes={types || []}
                onSubmit={handleUpdate}
                onCancel={() => setShowEditModal(false)}
                isSubmitting={updateMutation.isPending}
              />
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-navy/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-lifted w-full max-w-md p-6">
            <h2 className="text-xl font-display font-semibold text-navy mb-2">Remove Team Member?</h2>
            <p className="text-warm-gray mb-6">
              Are you sure you want to remove {member.name} from your care team? This action cannot be undone.
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-navy hover:bg-cream rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-500 text-white hover:bg-red-600 rounded-lg transition-colors disabled:opacity-50"
              >
                {deleteMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CareTeamMemberPage;


